import API from '../Services/API';
import Model from './Model';
import State from './State';
import { toQueryString } from '../Services/helpers';
import {
    buildPowerSearchCacheKey,
    buildPowerSearchRequest,
    hasPowerSearchCriteria,
} from './powerSearchHelpers';

const questionnaireStatusThemes = {
    'Draft': 'secondary',
    'Active': 'primary',
    'Awaiting Review': 'warning',
    'Requires Update': 'warning',
    'Completed': 'success',
    'Declined': 'danger',
    'Cancelled': 'danger',
    'Expired': 'danger',
};

const responseStatusThemes = {
    'Not Started': 'secondary',
    'In Progress': 'info',
    'Submitted': 'success',
};

const editableQuestionnaireStatuses = ['Active', 'Requires Update'];

const completableQuestionnaireStatuses = ['Active', 'Awaiting Review', 'Requires Update'];

const markAwaitingReviewQuestionnaireStatuses = ['Active', 'Requires Update'];

const canEditQuestionnaireResponse = (questionnaire) => {
    return editableQuestionnaireStatuses.includes(questionnaire?.status);
};

const isAwaitingQuestionnaireResponse = (questionnaire) => {
    return canEditQuestionnaireResponse(questionnaire);
};

const canEditQuestionnaireFields = (questionnaire) => {
    return !['Completed', 'Declined', 'Cancelled', 'Expired'].includes(questionnaire?.status);
};

const submittedQuestionnaireStatuses = ['Awaiting Review', 'Completed'];

const showThankYouMessage = (questionnaire) => {
    return submittedQuestionnaireStatuses.includes(questionnaire?.status) && !!questionnaire?.thankYouMessage;
};

const isQuestionnaireDeclinable = (questionnaire) => {
    return !!questionnaire?.declineConfig?.enabled;
};

const isQuestionnaireDeclined = (questionnaire) => {
    return questionnaire?.status === 'Declined';
};

const questionnaireTitle = (questionnaire, fallback = 'Questionnaire') => {
    return questionnaire?.title || questionnaire?.templateKey || fallback;
};

const questionnaireLatestActivity = (questionnaire) => {
    if (questionnaire.submittedAt && questionnaire.submittedAt >= questionnaire.updatedAt) {
        return { label: 'Submitted', date: questionnaire.submittedAt };
    }

    if (questionnaire.updatedAt && questionnaire.updatedAt !== questionnaire.createdAt) {
        return { label: 'Updated', date: questionnaire.updatedAt };
    }

    return { label: 'Assigned', date: questionnaire.createdAt };
};

const questionnaireUserName = (questionnaire) => {
    const name = [questionnaire.user?.firstName, questionnaire.user?.lastName].filter(Boolean).join(' ');

    return name || questionnaire.user?.email || null;
};

const extractQuestionnaireAnswers = (formData, fields) => {
    const answerableFieldIds = (fields ?? [])
        .filter((field) => field.type !== 'section')
        .map((field) => field.id);

    return Object.fromEntries(
        Object.entries(formData).filter(([fieldId]) => answerableFieldIds.includes(fieldId)),
    );
};

const useQuestionnaires = (searchData, alwaysRefresh = false) => {
    const searchStr = toQueryString(searchData || {});
    const cacheKey = buildPowerSearchCacheKey(searchData, searchStr);
    const usePostSearch = hasPowerSearchCriteria(searchData);

    return Model({
        alwaysRefresh,
        stateKey: ['admin', 'questionnaires', cacheKey],
        fetchData: () => {
            if (usePostSearch) {
                return API.post('/admin/questionnaires/search', buildPowerSearchRequest(searchData));
            }

            return API.get('/admin/questionnaires?' + searchStr);
        },
        returnDataKey: 'questionnaires',
        saveAdditionalData: (questionnaires) => {
            questionnaires.forEach((questionnaire) => {
                State.putIfNotExists(['admin', 'questionnaire', questionnaire.id], questionnaire);
            });
        },
        defaultLoadingError: 'Sorry, there was an error loading questionnaires.',
    });
};

const useMyQuestionnaires = (alwaysRefresh = false) => {
    return Model({
        alwaysRefresh,
        stateKey: ['myQuestionnaires'],
        fetchData: () => API.get('/questionnaires/mine'),
        returnDataKey: 'questionnaires',
        saveAdditionalData: (questionnaires) => {
            questionnaires.forEach((questionnaire) => {
                State.putIfNotExists(['questionnaire', questionnaire.id], questionnaire);
            });
        },
        defaultLoadingError: 'Sorry, there was an error loading your questionnaires.',
    });
};

const useQuestionnaire = (questionnaireId, alwaysRefresh = false, admin = false) => {
    return Model({
        alwaysRefresh,
        stateKey: admin ? ['admin', 'questionnaire', questionnaireId] : ['questionnaire', questionnaireId],
        block: !questionnaireId,
        fetchData: () => API.get(admin ? `/admin/questionnaires/${questionnaireId}` : `/questionnaires/${questionnaireId}`),
        returnDataKey: 'questionnaire',
        defaultLoadingError: 'Sorry, there was an error loading the questionnaire.',
        registerAdditionalMethods: (opts) => {
            return {
                saveResponse: (answers) => {
                    return opts.wrapUpdateCall(
                        () => API.put(`/questionnaires/${questionnaireId}/response`, { answers }),
                        'Your answers have been saved.',
                        true,
                    );
                },
                submitResponse: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/questionnaires/${questionnaireId}/response/submit`),
                        'Your questionnaire has been submitted.',
                        true,
                    );
                },
                declineResponse: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/questionnaires/${questionnaireId}/decline`),
                        'You have declined this questionnaire.',
                        true,
                    );
                },
                activate: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/questionnaires/${questionnaireId}/activate`),
                        'The questionnaire has been activated.',
                        true,
                    );
                },
                cancel: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/questionnaires/${questionnaireId}/cancel`),
                        'The questionnaire has been cancelled.',
                        true,
                    );
                },
                requireUpdate: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/questionnaires/${questionnaireId}/requireUpdate`),
                        'The questionnaire has been sent back for updates.',
                        true,
                    );
                },
                markAwaitingReview: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/questionnaires/${questionnaireId}/awaitingReview`),
                        'The questionnaire has been marked as awaiting review.',
                        true,
                    );
                },
                complete: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/questionnaires/${questionnaireId}/complete`),
                        'The questionnaire has been completed.',
                        true,
                    );
                },
                updateQuestionnaire: (payload) => {
                    return opts.wrapUpdateCall(
                        () => API.put(`/questionnaires/${questionnaireId}`, payload),
                        'The questionnaire has been updated.',
                        true,
                    );
                },
                addField: (field) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/questionnaires/${questionnaireId}/fields`, field),
                        'The field has been added.',
                        true,
                    );
                },
                updateField: (fieldId, field) => {
                    return opts.wrapUpdateCall(
                        () => API.put(`/questionnaires/${questionnaireId}/fields/${fieldId}`, field),
                        'The field has been updated.',
                        true,
                    );
                },
                removeField: (fieldId) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/questionnaires/${questionnaireId}/fields/${fieldId}`),
                        'The field has been removed.',
                        true,
                    );
                },
            };
        },
    });
};

const useQuestionnaireResponse = (questionnaireId, alwaysRefresh = false) => {
    return Model({
        alwaysRefresh,
        stateKey: ['questionnaireResponse', questionnaireId],
        block: !questionnaireId,
        fetchData: () => API.get(`/questionnaires/${questionnaireId}/response`),
        returnDataKey: 'response',
        defaultLoadingError: 'Sorry, there was an error loading the questionnaire response.',
    });
};

const useQuestionnaireTemplates = (alwaysRefresh = false) => {
    return Model({
        alwaysRefresh,
        stateKey: ['admin', 'questionnaireTemplates'],
        fetchData: () => API.get('/admin/questionnaires/templates'),
        returnDataKey: 'templates',
        defaultLoadingError: 'Sorry, there was an error loading questionnaire templates.',
    });
};

const questionnaireStatuses = Object.keys(questionnaireStatusThemes);
const responseStatuses = Object.keys(responseStatusThemes);

export {
    useQuestionnaires,
    useMyQuestionnaires,
    useQuestionnaire,
    useQuestionnaireResponse,
    useQuestionnaireTemplates,
    questionnaireStatusThemes,
    responseStatusThemes,
    questionnaireStatuses,
    responseStatuses,
    canEditQuestionnaireResponse,
    canEditQuestionnaireFields,
    showThankYouMessage,
    isQuestionnaireDeclinable,
    isQuestionnaireDeclined,
    completableQuestionnaireStatuses,
    markAwaitingReviewQuestionnaireStatuses,
    isAwaitingQuestionnaireResponse,
    extractQuestionnaireAnswers,
    questionnaireTitle,
    questionnaireLatestActivity,
    questionnaireUserName,
};
